import { z } from 'zod';
import { OBJECT_NAMES } from './objects';

const uuid = z.string().uuid();
const money = z.number().finite().min(0).max(1e12);
const optText = (max: number) => z.preprocess((v) => (v === '' ? null : v), z.string().trim().max(max).nullable()).optional();

// --- Products and price books --------------------------------------------------
export const productSchema = z
  .object({
    name: z.string().trim().min(1).max(255),
    code: optText(64),
    family: optText(80),
    /** Manufacturer brand (matched to the CRM's brand list to suggest quoted brands). */
    brand: optText(80),
    description: optText(4000),
    isActive: z.boolean().default(true),
    /** Required when creating: the product's list price in the standard price book. */
    listPrice: money.optional(),
  })
  .strict();
export interface Product { id: string; name: string; code: string | null; family: string | null; brand: string | null; description: string | null; isActive: boolean; listPrice: number | null }

export const priceBookSchema = z.object({ name: z.string().trim().min(1).max(120), description: optText(1000), isActive: z.boolean().default(true) }).strict();
export interface PriceBook { id: string; name: string; description: string | null; isStandard: boolean; isActive: boolean; entryCount: number }

export const priceBookEntrySchema = z.object({ productId: uuid, unitPrice: money, isActive: z.boolean().default(true) }).strict();
export interface PriceBookEntry { id: string; priceBookId: string; productId: string; productName: string; productCode: string | null; unitPrice: number; isActive: boolean }

// --- Line items (opportunity and quote share the shape) -----------------------------
export const lineItemSchema = z
  .object({
    priceBookEntryId: uuid,
    quantity: z.number().positive().max(1e9),
    /** Defaults to the price book entry's price. */
    unitPrice: money.optional(),
    discountPct: z.number().min(0).max(100).default(0),
    description: optText(1000),
  })
  .strict();
export const lineItemUpdateSchema = z
  .object({ quantity: z.number().positive().max(1e9).optional(), unitPrice: money.optional(), discountPct: z.number().min(0).max(100).optional(), description: optText(1000) })
  .strict();
export interface LineItem {
  id: string;
  priceBookEntryId: string;
  productId: string;
  productName: string;
  productCode: string | null;
  quantity: number;
  unitPrice: number;
  discountPct: number;
  total: number;
  description: string | null;
}

// --- Quotes -----------------------------------------------------------------------------
export const QUOTE_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'presented', label: 'Presented' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'denied', label: 'Denied' },
] as const;
export const quoteCreateSchema = z
  .object({
    name: z.string().trim().min(1).max(255),
    expirationDate: z.preprocess((v) => (v === '' ? null : v), z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable()).optional(),
    taxPct: z.number().min(0).max(100).default(0),
    notes: optText(4000),
    terms: optText(8000),
    /** Copy the opportunity's products onto the quote. */
    copyLineItems: z.boolean().default(true),
  })
  .strict();
export const quoteUpdateSchema = quoteCreateSchema
  .omit({ copyLineItems: true })
  .partial()
  .extend({ status: z.enum(['draft', 'presented', 'accepted', 'denied']).optional() })
  .strict();
export interface Quote {
  id: string;
  opportunityId: string;
  opportunityName: string;
  accountName: string | null;
  number: string;
  name: string;
  status: 'draft' | 'presented' | 'accepted' | 'denied';
  expirationDate: string | null;
  taxPct: number;
  notes: string | null;
  terms: string | null;
  isSyncing: boolean;
  priceBookId: string | null;
  version: number;
  subtotal: number;
  tax: number;
  grandTotal: number;
  createdAt: string;
}

// --- Import ------------------------------------------------------------------------------
export const importSchema = z
  .object({
    objectName: z.enum(OBJECT_NAMES),
    fileName: z.string().trim().min(1).max(255),
    /** Raw CSV text (UTF-8), at most 5 MB. */
    csv: z.string().min(1).max(5 * 1024 * 1024),
    /** CSV header → field name (unmapped columns are ignored). */
    mapping: z.record(z.string().max(255), z.string().max(64)).refine((m) => Object.keys(m).length > 0, 'Map at least one column'),
    /** How to treat rows matching an existing record (by email for leads/contacts, name for accounts). */
    duplicates: z.enum(['skip', 'update', 'create']).default('skip'),
    dryRun: z.boolean().default(false),
    /** Companies named in a Company column that don't exist yet are created (once per name). */
    createAccounts: z.boolean().default(false),
  })
  .strict();
export type ImportInput = z.infer<typeof importSchema>;
export interface ImportJob {
  id: string;
  objectName: string;
  fileName: string;
  dryRun: boolean;
  status: 'queued' | 'running' | 'completed' | 'failed';
  totalRows: number;
  processed: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  errors: { row: number; message: string }[];
  createdAt: string;
  finishedAt: string | null;
}
export const IMPORT_MAX_ROWS = 10_000;

// --- Search -----------------------------------------------------------------------------
export interface SearchHit { objectName: string; recordId: string; title: string; subtitle: string | null; rank: number }

// --- API tokens and MFA -----------------------------------------------------------
export const apiTokenSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    scopes: z.array(z.enum(['read', 'write'])).min(1).max(2),
    expiresInDays: z.number().int().min(1).max(365).nullable().default(90),
  })
  .strict();
export interface ApiToken { id: string; name: string; prefix: string; scopes: string[]; lastUsedAt: string | null; expiresAt: string | null; createdAt: string }

export const totpCodeSchema = z.object({ code: z.string().trim().regex(/^\d{6}$/, 'Enter the 6-digit code') }).strict();
export const mfaVerifySchema = z
  .object({ challenge: z.string().min(20).max(100), code: z.string().trim().regex(/^\d{6}$/).optional(), recoveryCode: z.string().trim().min(8).max(32).optional() })
  .strict()
  .refine((v) => !!v.code !== !!v.recoveryCode, { message: 'Enter a code from your app or a recovery code' });
